import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/lib/auth";
import { STEPS } from "@/lib/content";

export default function HowItWorks() {
  const { isAuthed, user } = useAuth();
  const nav = useNavigate();
  const enter = () => nav(isAuthed && user ? homeFor(user) : "/login");

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-14 pt-10 pb-24">
      <div className="max-w-2xl">
        <div className="overline text-cobalt-500">How it works</div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-black tracking-tighter">
          From raised to resolved &mdash; automatically.
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          Three steps, no manual triage. Here's exactly what happens to a ticket from the moment it's raised.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-3 gap-5">
        {STEPS.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.08 }}
            className="glass rounded-2xl p-6 relative overflow-hidden"
          >
            <div className="absolute -top-6 -right-4 font-display font-black text-[90px] leading-none text-cobalt-500/10 select-none">{s.n}</div>
            <div className="font-mono text-xs tracking-widest text-cobalt-500">{s.n}</div>
            <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tighter">{s.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.long}</p>
          </motion.div>
        ))}
      </div>

      <div className="glass rounded-3xl p-5 flex items-center gap-4 mt-6">
        <Zap className="h-5 w-5 text-cobalt-500 flex-none" />
        <div className="text-sm">
          <span className="font-medium">Real-time, end to end. </span>
          <span className="text-muted-foreground">Tickets, dashboards, and notifications update live as your team works — no page refresh needed.</span>
        </div>
      </div>

      <div className="mt-14 glass-strong rounded-3xl p-10 lg:p-12 text-center">
        <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tighter">
          Ready to see it on your own tickets?
        </h2>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Sign in to your institute's workspace, or explore the full feature set first.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button onClick={enter} size="lg"
            className="rounded-full font-medium h-11 px-6 bg-zinc-900 hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100">
            {isAuthed ? "Go to your dashboard" : "Sign in to Solution"} <ArrowUpRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Link to="/features">
            <Button variant="outline" size="lg" className="rounded-full font-medium h-11 px-6">
              Explore features
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
